/**
 * Version Management Modal
 * Allows admins to create and edit software versions
 */

import { useState, useEffect } from 'react';
import {
  useCreateVersionMutation,
  useUpdateVersionMutation,
  useDeleteVersionMutation,
} from '../services/api';
import type { SoftwareVersion } from '@patch-manager/shared';

interface VersionManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  softwareId: number;
  softwareName: string;
  version?: SoftwareVersion; // If provided, we're editing; otherwise, creating
}

export function VersionManageModal({
  isOpen,
  onClose,
  softwareId,
  softwareName,
  version,
}: VersionManageModalProps) {
  const [createVersion, { isLoading: isCreating }] = useCreateVersionMutation();
  const [updateVersion, { isLoading: isUpdating }] = useUpdateVersionMutation();
  const [deleteVersion, { isLoading: isDeleting }] = useDeleteVersionMutation();

  const [formData, setFormData] = useState({
    version_number: '',
    release_date: '',
    os_platform: 'windows',
    architecture: 'x64',
    download_url: '',
    release_notes_url: '',
    file_size_bytes: '',
    is_security_update: false,
    release_channel: 'stable',
    display_version: '',
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Populate form when editing
  useEffect(() => {
    console.log('VersionManageModal useEffect triggered', { version, isOpen });
    if (version) {
      console.log('Populating form with version data:', {
        version_number: version.version_number,
        release_date: version.release_date,
        os_platform: version.os_platform,
        architecture: version.architecture,
        id: version.id,
      });
      setFormData({
        version_number: version.version_number || '',
        release_date: version.release_date || '',
        os_platform: version.os_platform || 'windows',
        architecture: version.architecture || 'x64',
        download_url: version.download_url || '',
        release_notes_url: version.release_notes_url || '',
        file_size_bytes: version.file_size_bytes?.toString() || '',
        is_security_update: version.is_security_update || false,
        release_channel: version.release_channel || 'stable',
        display_version: version.display_version || '',
      });
    } else {
      console.log('Resetting form for new version');
      // Reset form for new version
      setFormData({
        version_number: '',
        release_date: '',
        os_platform: 'windows',
        architecture: 'x64',
        download_url: '',
        release_notes_url: '',
        file_size_bytes: '',
        is_security_update: false,
        release_channel: 'stable',
        display_version: '',
      });
    }
  }, [version, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const versionData: any = {
        ...formData,
        file_size_bytes: formData.file_size_bytes ? parseInt(formData.file_size_bytes) : null,
        display_version: formData.display_version || formData.version_number,
      };

      if (version) {
        // Update existing version
        await updateVersion({ versionId: version.id, version: versionData }).unwrap();
        alert('Version updated successfully!');
      } else {
        // Create new version
        await createVersion({ softwareId, version: versionData }).unwrap();
        alert('Version created successfully!');
      }

      onClose();
    } catch (error: any) {
      console.error('Failed to save version:', error);
      alert(`Failed to save version: ${error.data?.error || error.message}`);
    }
  };

  const handleDelete = async () => {
    if (!version) return;

    try {
      await deleteVersion(version.id).unwrap();
      alert('Version deleted successfully!');
      onClose();
    } catch (error: any) {
      console.error('Failed to delete version:', error);
      alert(`Failed to delete version: ${error.data?.error || error.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {version ? (
              <>
                Edit Version
                <span className="text-sm font-normal text-gray-400 ml-2">
                  ({version.os_platform} {version.architecture})
                </span>
              </>
            ) : (
              'Add New Version'
            )}
          </h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Software</label>
            <input type="text" value={softwareName} disabled className="form-control" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Version Number *</label>
              <input
                type="text"
                className="form-control"
                value={formData.version_number}
                onChange={(e) => setFormData({ ...formData, version_number: e.target.value })}
                required
                placeholder="e.g., 143.0.7499.192"
              />
            </div>

            <div className="form-group">
              <label>Display Version</label>
              <input
                type="text"
                className="form-control"
                value={formData.display_version}
                onChange={(e) => setFormData({ ...formData, display_version: e.target.value })}
                placeholder="Optional, defaults to version number"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>OS Platform *</label>
              <select
                className="form-control"
                value={formData.os_platform}
                onChange={(e) => setFormData({ ...formData, os_platform: e.target.value })}
                required
              >
                <option value="windows">Windows</option>
                <option value="macos">macOS</option>
                <option value="linux">Linux</option>
                <option value="cross-platform">Cross-Platform</option>
              </select>
            </div>

            <div className="form-group">
              <label>Architecture *</label>
              <select
                className="form-control"
                value={formData.architecture}
                onChange={(e) => setFormData({ ...formData, architecture: e.target.value })}
                required
              >
                <option value="x64">x64</option>
                <option value="x86">x86</option>
                <option value="arm64">ARM64</option>
                <option value="universal">Universal</option>
              </select>
            </div>

            <div className="form-group">
              <label>Release Channel</label>
              <select
                className="form-control"
                value={formData.release_channel}
                onChange={(e) => setFormData({ ...formData, release_channel: e.target.value })}
              >
                <option value="stable">Stable</option>
                <option value="beta">Beta</option>
                <option value="alpha">Alpha</option>
                <option value="preview">Preview</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Release Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.release_date}
              onChange={(e) => setFormData({ ...formData, release_date: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Download URL</label>
            <input
              type="url"
              className="form-control"
              value={formData.download_url}
              onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label>Release Notes URL</label>
            <input
              type="url"
              className="form-control"
              value={formData.release_notes_url}
              onChange={(e) => setFormData({ ...formData, release_notes_url: e.target.value })}
              placeholder="https://..."
            />
            <small className="form-text">
              Release notes will be automatically indexed for AI analysis
            </small>
          </div>

          <div className="form-group">
            <label>File Size (bytes)</label>
            <input
              type="number"
              className="form-control"
              value={formData.file_size_bytes}
              onChange={(e) => setFormData({ ...formData, file_size_bytes: e.target.value })}
              placeholder="e.g., 104857600"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_security_update}
                onChange={(e) => setFormData({ ...formData, is_security_update: e.target.checked })}
              />
              <span>Security Update</span>
            </label>
          </div>

          <div className="modal-footer">
            <div className="footer-left">
              {version && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Version'}
                </button>
              )}
            </div>
            <div className="footer-right">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isCreating || isUpdating}
              >
                {isCreating || isUpdating ? 'Saving...' : version ? 'Update Version' : 'Create Version'}
              </button>
            </div>
          </div>
        </form>

        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <div className="confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Delete</h3>
              <p>
                Are you sure you want to delete version <strong>{version?.version_number}</strong>?
                This will also remove all CVE associations.
              </p>
              <div className="confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.7);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .modal-content {
            background-color: white;
            border-radius: 8px;
            max-width: 600px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #e0e0e0;
          }

          .modal-header h2 {
            margin: 0;
            font-size: 1.5rem;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 2rem;
            cursor: pointer;
            color: #666;
          }

          .close-button:hover {
            color: #000;
          }

          .modal-body {
            padding: 20px;
          }

          .form-group {
            margin-bottom: 16px;
          }

          .form-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 16px;
          }

          label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #333;
          }

          .form-control {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            color: #333;
            background-color: #fff;
          }

          .form-control:disabled {
            background-color: #f5f5f5;
            color: #666;
            cursor: not-allowed;
          }

          .form-control:focus {
            outline: none;
            border-color: #0066cc;
            box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
          }

          .form-text {
            display: block;
            margin-top: 4px;
            font-size: 12px;
            color: #666;
          }

          .checkbox-label {
            display: flex;
            align-items: center;
            cursor: pointer;
          }

          .checkbox-label input[type="checkbox"] {
            margin-right: 8px;
            width: auto;
          }

          .modal-footer {
            display: flex;
            justify-content: space-between;
            padding-top: 20px;
            border-top: 1px solid #e0e0e0;
            margin-top: 20px;
          }

          .footer-left,
          .footer-right {
            display: flex;
            gap: 8px;
          }

          .btn {
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s;
          }

          .btn-primary {
            background-color: #0066cc;
            color: white;
          }

          .btn-primary:hover:not(:disabled) {
            background-color: #0052a3;
          }

          .btn-secondary {
            background-color: #6c757d;
            color: white;
          }

          .btn-secondary:hover {
            background-color: #5a6268;
          }

          .btn-danger {
            background-color: #dc3545;
            color: white;
          }

          .btn-danger:hover:not(:disabled) {
            background-color: #c82333;
          }

          .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .confirm-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1100;
          }

          .confirm-dialog {
            background-color: white;
            padding: 24px;
            border-radius: 8px;
            max-width: 400px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }

          .confirm-dialog h3 {
            margin: 0 0 16px 0;
          }

          .confirm-dialog p {
            margin: 0 0 24px 0;
            color: #666;
          }

          .confirm-actions {
            display: flex;
            justify-content: flex-end;
            gap: 8px;
          }
        `}</style>
      </div>
    </div>
  );
}
